<template>
  <!-- Settings › General as the app draws it today (Workspace369 origin/main, Oct 23 2026), at the app's own scale.
       Wide containers get the settings shell (lib/view/settings/registry/settings_registry.dart: Back to app, the
       workspace with its plan, Search settings, then the You / Workspace / Sales groups and the Help & about, Log out
       footer) around lib/view/workspace_details/general/: the page scrolled to Regional (Currency, Time zone, Date
       format, Time format, First day of week, Unit system, Document language and its note) and Tax codes. The currency
       select opens widgets/workspace_currency_picker.dart: on desktop a dialog (max 540×650, header icon:
       the currency-exchange glyph, "Search by currency or code", rows of code, name and symbol, the selected row tinted with a
       check), on a phone a bottom sheet with a drag handle that focuses the search, so the keyboard is up. The list is
       currency_picker's own (most traded first); "can" finds CAD, XAF, DOP and MAD, and those codes have no
       two-letter flag, so the app draws ¤ in the flag column. Picking saves at once and the toaster says "Saved"
       (general_saves.dart). The picker's subtitle is left out. Labels are app_en.arb strings; Cedar & Pine Design,
       its details and tax codes are sample data. States: `picker` (the dialog on USD), `search` ("can" typed) and
       `saved` (CAD · Canadian Dollar under Regional, the Saved toast). The server renders `saved`. -->
  <div class="mcw" :class="`mcw--${state}`" aria-hidden="true">
    <svg class="mcw-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`mcw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="mcw-desk">
      <aside class="mcw-rail">
        <div class="mcw-back"><svg class="ic sm"><use href="#mcw-chevronLeft" /></svg>Back to app</div>
        <div class="mcw-ws"><span class="mcw-logo">C</span><b class="mcw-ell">Cedar &amp; Pine Design</b><em class="mcw-chip">Voyager</em></div>
        <div class="mcw-rsearch"><svg class="ic xs"><use href="#mcw-search" /></svg>Search settings</div>
        <div class="mcw-groups">
          <template v-for="g in rail" :key="g.k">
            <div class="mcw-gl">{{ g.k }}</div>
            <div v-for="r in g.rows" :key="r.k" class="mcw-ri" :class="{ sel: r.k === 'General' }">
              <svg class="ic sm"><use :href="`#mcw-${r.i}`" /></svg><span class="mcw-ell">{{ r.k }}</span>
              <svg v-if="r.ext" class="ic xs mcw-ext"><use href="#mcw-openInNew" /></svg>
            </div>
          </template>
        </div>
        <div class="mcw-rfoot">
          <div class="mcw-ri"><svg class="ic sm"><use href="#mcw-helpOutline" /></svg><span>Help &amp; about</span></div>
          <div class="mcw-ri"><svg class="ic sm"><use href="#mcw-logout" /></svg><span>Log out</span></div>
        </div>
      </aside>

      <div class="mcw-main">
        <header class="mcw-crumb"><svg class="ic sm"><use href="#mcw-storefront" /></svg><span>Workspace</span><i>/</i><b>General</b></header>
        <div class="mcw-scroll">
          <div class="mcw-page">
            <div class="mcw-gh">Regional</div>
            <div class="mcw-card">
              <div class="mcw-row">
                <span class="mcw-fl">Currency</span>
                <span :key="`cur-${currency.code}`" class="mcw-sel" :class="{ 'is-new': saved }"><span class="mcw-ell">{{ currency.code }} · {{ currency.name }}</span><svg class="ic sm"><use href="#mcw-expand_more" /></svg></span>
              </div>
              <div v-for="r in regional" :key="r.l" class="mcw-row">
                <span class="mcw-fl">{{ r.l }}</span>
                <span v-if="r.seg" class="mcw-seg"><span v-for="s in r.seg" :key="s" :class="{ on: s === r.v }">{{ s }}</span></span>
                <span v-else class="mcw-sel"><span class="mcw-ell">{{ r.v }}</span><svg class="ic sm"><use href="#mcw-expand_more" /></svg></span>
              </div>
            </div>
            <p class="mcw-note">Document language sets the language and formats of invoices and other documents sent to clients.</p>

            <div class="mcw-th">
              <div><div class="mcw-gh">Tax codes</div><p>Reusable tax rates for your invoices.</p></div>
              <span class="mcw-obtn"><svg class="ic sm"><use href="#mcw-add" /></svg>Add tax code</span>
            </div>
            <div class="mcw-card">
              <div v-for="t in taxCodes" :key="t.n" class="mcw-tax">
                <div class="mcw-tn"><b>{{ t.n }}</b><em v-if="t.def" class="mcw-def">Default</em><small v-if="t.parts">{{ t.parts }}</small></div>
                <b>{{ t.rate }}</b><svg class="ic sm"><use href="#mcw-more_horiz" /></svg>
              </div>
            </div>
          </div>
        </div>
        <div v-if="saved" class="mcw-toast"><svg class="ic sm mcw-tok"><use href="#mcw-check_circle_outline" /></svg><span>Saved</span><svg class="ic sm mcw-tx"><use href="#mcw-close" /></svg></div>
      </div>

      <div v-if="!saved" class="mcw-barrier">
        <div :key="`dlg-${state}`" class="mcw-dlg" :class="{ 'is-in': state === 'picker' }">
          <div class="mcw-dh">
            <span class="mcw-fi"><svg class="ic"><use href="#mcw-currencyExchange" /></svg></span>
            <b>Choose Currency</b>
            <svg class="ic mcw-dx"><use href="#mcw-close" /></svg>
          </div>
          <div class="mcw-field" :class="{ typed: query }">
            <svg class="ic"><use href="#mcw-search" /></svg>
            <span v-if="query" class="mcw-q">{{ query }}<i class="mcw-caret" /></span>
            <span v-else class="mcw-ph">Search by currency or code</span>
            <svg v-if="query" class="ic sm mcw-qx"><use href="#mcw-close" /></svg>
          </div>
          <div class="mcw-list">
            <div v-for="(c, i) in list" :key="c.code" class="mcw-cr" :class="{ sel: c.code === 'USD', hover: state === 'search' && i === 0 }">
              <span class="mcw-flag">¤</span>
              <span class="mcw-cn"><b>{{ c.code }}</b><small class="mcw-ell">{{ c.name }}</small></span>
              <span class="mcw-sym">{{ c.sym }}</span>
              <svg v-if="c.code === 'USD'" class="ic mcw-ck"><use href="#mcw-checkCircle" /></svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Phone -->
    <div class="mcw-phone">
      <div class="mcp-top"><svg class="ic"><use href="#mcw-arrowBackIos" /></svg><b>General</b></div>
      <div class="mcp-body">
        <div class="mcp-gl">Regional</div>
        <div class="mcp-card">
          <div class="mcp-row" :class="{ 'is-new': saved }">
            <span><small>Currency</small><b :key="`pc-${currency.code}`">{{ currency.code }} · {{ currency.name }}</b></span>
            <svg class="ic"><use href="#mcw-chevron_right" /></svg>
          </div>
          <template v-for="r in regional" :key="`p-${r.l}`">
            <div v-if="r.seg" class="mcp-row seg">
              <small>{{ r.l }}</small>
              <span class="mcw-seg"><span v-for="s in r.seg" :key="s" :class="{ on: s === r.v }">{{ s }}</span></span>
            </div>
            <div v-else class="mcp-row">
              <span><small>{{ r.l }}</small><b>{{ r.v }}</b></span>
              <svg class="ic"><use href="#mcw-chevron_right" /></svg>
            </div>
          </template>
        </div>
        <p class="mcp-note">Document language sets the language and formats of invoices and other documents sent to clients.</p>
        <div class="mcp-gl">Tax codes</div>
        <div class="mcp-card">
          <div v-for="t in taxCodes" :key="`p-${t.n}`" class="mcp-tax">
            <div class="mcw-tn"><b>{{ t.n }}</b><em v-if="t.def" class="mcw-def">Default</em><small v-if="t.parts">{{ t.parts }}</small></div>
            <b>{{ t.rate }}</b><svg class="ic"><use href="#mcw-more_horiz" /></svg>
          </div>
          <div class="mcp-add"><svg class="ic"><use href="#mcw-add" /></svg>Add tax code</div>
        </div>
      </div>
      <div v-if="saved" class="mcw-toast"><svg class="ic mcw-tok"><use href="#mcw-check_circle_outline" /></svg><span>Saved</span><svg class="ic mcw-tx"><use href="#mcw-close" /></svg></div>

      <div v-if="!saved" class="mcp-barrier">
        <div class="mcp-sheet" :class="{ 'is-in': state === 'picker' }">
          <i class="mcp-handle" />
          <div class="mcw-dh">
            <span class="mcw-fi"><svg class="ic"><use href="#mcw-currencyExchange" /></svg></span>
            <b>Choose Currency</b>
            <svg class="ic mcw-dx"><use href="#mcw-close" /></svg>
          </div>
          <div class="mcw-field typed">
            <svg class="ic"><use href="#mcw-search" /></svg>
            <span v-if="query" class="mcw-q">{{ query }}<i class="mcw-caret" /></span>
            <span v-else class="mcw-ph"><i class="mcw-caret" />Search by currency or code</span>
            <svg v-if="query" class="ic mcw-qx"><use href="#mcw-close" /></svg>
          </div>
          <div class="mcw-list">
            <div v-for="c in list" :key="`p-${c.code}`" class="mcw-cr" :class="{ sel: c.code === 'USD' }">
              <span class="mcw-flag">¤</span>
              <span class="mcw-cn"><b>{{ c.code }}</b><small class="mcw-ell">{{ c.name }}</small></span>
              <span class="mcw-sym">{{ c.sym }}</span>
              <svg v-if="c.code === 'USD'" class="ic mcw-ck"><use href="#mcw-checkCircle" /></svg>
            </div>
          </div>
        </div>
        <div class="mcp-kb">
          <div v-for="(row, i) in keys" :key="i" class="mcp-kr">
            <span v-if="i === 2" class="mcp-k fn">⇧</span>
            <span v-for="k in row" :key="k" class="mcp-k">{{ k }}</span>
            <span v-if="i === 2" class="mcp-k fn">⌫</span>
          </div>
          <div class="mcp-kr"><span class="mcp-k fn w2">123</span><span class="mcp-k space">space</span><span class="mcp-k go w2">search</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { currencyIcons } from "~/data/app-icons-currency.js";

const props = defineProps({
  // picker | search | saved
  state: { type: String, default: "saved" },
});

const usedApp = ["add", "check_circle_outline", "chevron_right", "description", "expand_more", "history_edu", "inventory_2", "logout", "more_horiz", "notifications", "receipt_long", "search"];
const icons = { ...Object.fromEntries(usedApp.map((k) => [k, appIcons[k]])), ...currencyIcons };

const rail = [
  { k: "You", rows: [
    { k: "Account", i: "accountCircle" }, { k: "Appearance", i: "palette" }, { k: "Notifications", i: "notifications" },
    { k: "Customize", i: "dashboardCustomize" }, { k: "Device drafts", i: "save" },
  ] },
  { k: "Workspace", rows: [
    { k: "General", i: "storefront" }, { k: "Brand & looks", i: "style" }, { k: "Team & seats", i: "peopleOutline" },
    { k: "Billing", i: "creditCard" }, { k: "Plan", i: "workspacePremium", ext: true }, { k: "Online payments", i: "accountBalance" },
    { k: "After payment", i: "taskAlt" }, { k: "Files", i: "folder" },
  ] },
  { k: "Sales", rows: [
    { k: "Clients", i: "contactPage" }, { k: "Invoices", i: "receipt_long" }, { k: "Proposals", i: "description" },
    { k: "Contracts", i: "history_edu" }, { k: "Products", i: "inventory_2" },
  ] },
];

const saved = computed(() => props.state === "saved");
const query = computed(() => (props.state === "search" ? "can" : ""));
const currency = computed(() => (saved.value ? { code: "CAD", name: "Canadian Dollar" } : { code: "USD", name: "United States Dollar" }));

// currency_picker 2.0.22's list order, and what "can" matches in code, name or symbol.
const all = [
  { code: "USD", name: "United States Dollar", sym: "$" }, { code: "EUR", name: "Euro", sym: "€" },
  { code: "JPY", name: "Japanese Yen", sym: "¥" }, { code: "GBP", name: "British Pound", sym: "£" },
  { code: "AUD", name: "Australian Dollar", sym: "$" }, { code: "CAD", name: "Canadian Dollar", sym: "$" },
  { code: "CHF", name: "Switzerland Franc", sym: "CHF" }, { code: "CNY", name: "China Yuan Renminbi", sym: "¥" },
  { code: "HKD", name: "Hong Kong Dollar", sym: "$" }, { code: "NZD", name: "New Zealand Dollar", sym: "$" },
];
const found = [
  { code: "CAD", name: "Canadian Dollar", sym: "$" }, { code: "XAF", name: "Central African CFA franc", sym: "FCFA" },
  { code: "DOP", name: "Dominican Republic Peso", sym: "RD$" }, { code: "MAD", name: "Moroccan Dirham", sym: "MAD" },
];
const list = computed(() => (props.state === "search" ? found : all));

const regional = [
  { l: "Time zone", v: "America/Vancouver" },
  { l: "Date format", v: "YYYY-MM-DD (2024-01-15)" },
  { l: "Time format", v: "12-hour" },
  { l: "First day of week", v: "Sunday" },
  { l: "Unit system", seg: ["US (Imperial)", "Metric"], v: "Metric" },
  { l: "Document language", v: "English" },
];
const taxCodes = [
  { n: "GST", def: true, rate: "5.00%" },
  { n: "GST + PST", parts: "GST: 5.00% + PST: 7.00%", rate: "12.00%" },
];
const keys = ["qwertyuiop".split(""), "asdfghjkl".split(""), "zxcvbnm".split("")];
</script>

<style>
/* Design tokens: the settings surfaces as rendered by the app (rail, page, cards) on the shadcn Neutral scheme. */
.mcw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --rail: #e6e6e6; --canvas: #efefef; --card: #fafafa; --surface: #fff; --edge: #e7e7e7; --card-edge: #e3e3e3;
  --ink2: rgb(10 10 10 / 0.62); --ink3: rgb(10 10 10 / 0.5); --quiet: rgb(10 10 10 / 0.32); --selected: rgb(10 10 10 / 0.055); --hair: rgb(10 10 10 / 0.08);
  --tint: rgb(23 23 23 / 0.07); --green-t: #2d7a4e; --green-f: #e6f4ea; --blue: #2563eb;
  --kb: #d1d3d9; --key: #fff; --key-fn: #abb0bc;
  position: relative;
  container: mcw / inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .mcw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --rail: #161616; --canvas: #0e0e0e; --card: #242424; --surface: #1c1c1c; --edge: #262626; --card-edge: #2e2e2e;
  --ink2: rgb(250 250 250 / 0.62); --ink3: rgb(250 250 250 / 0.5); --quiet: rgb(250 250 250 / 0.32); --selected: rgb(250 250 250 / 0.1); --hair: rgb(250 250 250 / 0.1);
  --tint: rgb(250 250 250 / 0.07); --green-t: #6ed39a; --green-f: rgb(63 178 110 / 0.18); --blue: #5b8cf5;
  --kb: #2b2b2d; --key: #6b6b6e; --key-fn: #464649;
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow). */
.mcw { min-height: 640px; }
@media (max-width: 1128px) { .mcw { min-height: 560px; } }
@media (max-width: 648px) { .mcw { min-height: 0; aspect-ratio: 390 / 700; } }
.mcw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.mcw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.mcw .ic.sm { width: 16px; height: 16px; }
.mcw .ic.xs { width: 14px; height: 14px; }
.mcw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ---------- desktop settings shell ---------- */
.mcw-desk {
  position: relative;
  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  height: 640px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
}
.dark .mcw-desk, .dark .mcw-phone { border-color: #3a3a3a; }
.dark .mcw-desk { box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.mcw-rail { position: relative; display: flex; flex-direction: column; min-height: 0; border-right: 1px solid var(--edge); background: var(--rail); padding: 0 10px; }
.mcw-back { display: flex; align-items: center; gap: 8px; height: 40px; margin-top: 6px; padding: 0 10px; color: var(--ink2); font-size: 14px; }
.mcw-ws { display: flex; align-items: center; gap: 10px; height: 36px; border-radius: 8px; background: var(--selected); padding: 0 8px; font-size: 14px; }
.mcw-ws b { flex: 1; min-width: 0; font-weight: 600; }
.mcw-logo { display: grid; flex: none; place-items: center; width: 22px; height: 22px; border-radius: 5px; background: #171717; color: #fafafa; font-size: 11px; font-weight: 700; }
.dark .mcw-logo { background: #fafafa; color: #171717; }
.mcw-chip { flex: none; border-radius: 4px; background: var(--hair); padding: 1px 6px; color: var(--ink2); font-size: 12px; font-style: normal; }
.mcw-rsearch { display: flex; align-items: center; gap: 8px; height: 30px; margin: 8px 0 14px; border: 1px solid var(--edge); border-radius: 6px; background: var(--surface); padding: 0 10px; color: var(--ink3); }
.mcw-groups { flex: 1; min-height: 0; overflow: hidden; }
.mcw-gl { margin: 6px 0 4px; padding: 0 10px; color: var(--ink3); font-size: 11px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; }
.mcw-gl:not(:first-child) { margin-top: 14px; }
.mcw-ri { display: flex; align-items: center; gap: 10px; height: 32px; margin-bottom: 2px; border-radius: 8px; padding: 0 10px; color: var(--ink); font-weight: 500; white-space: nowrap; }
.mcw-ri > .ic { color: var(--ink2); }
.mcw-ri.sel { background: var(--selected); font-weight: 600; }
.mcw-ri .mcw-ext { margin-left: auto; color: var(--ink3); }
.mcw-rfoot { position: relative; padding: 8px 0 10px; box-shadow: 0 -10px 12px -8px rgb(0 0 0 / 0.12); }
.mcw-rfoot::before { content: ""; position: absolute; inset: 0 -10px; z-index: -1; background: var(--rail); }
.mcw-main { position: relative; display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.mcw-crumb { display: flex; align-items: center; gap: 8px; border-bottom: 1px solid var(--edge); padding: 0 20px; color: var(--ink2); }
.mcw-crumb i { color: var(--quiet); font-style: normal; }
.mcw-crumb b { color: var(--ink); font-weight: 600; }
.mcw-scroll { min-height: 0; overflow: hidden; -webkit-mask-image: linear-gradient(#000 88%, transparent); mask-image: linear-gradient(#000 88%, transparent); }

/* ---------- General › Regional and Tax codes (settings_group / settings_row) ---------- */
.mcw-page { max-width: 976px; padding: 22px 48px 0; }
.mcw-gh { margin-bottom: 8px; font-size: 14px; font-weight: 600; }
.mcw-card { overflow: hidden; border: 1px solid var(--card-edge); border-radius: 10px; background: var(--card); }
.mcw-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; height: 45px; border-top: 1px solid var(--hair); padding: 0 16px; }
.mcw-row:first-child { border-top: 0; }
.mcw-fl { flex: none; color: var(--ink2); font-size: 14px; }
.mcw-sel { display: inline-flex; align-items: center; gap: 6px; min-width: 0; max-width: 280px; height: 32px; border: 1px solid var(--card-edge); border-radius: 8px; background: var(--surface); padding: 0 8px 0 10px; font-weight: 600; }
.mcw-sel .ic { color: var(--ink2); }
.mcw-sel.is-new { animation: mcw-wash 2.4s ease-out both; }
.mcw-seg { display: inline-flex; gap: 2px; border-radius: 8px; background: var(--selected); padding: 3px; color: var(--ink3); font-size: 12px; font-weight: 600; }
.mcw-seg span { border-radius: 6px; padding: 5px 14px; white-space: nowrap; }
.mcw-seg .on { background: var(--surface); color: var(--ink); box-shadow: 0 1px 2px rgb(0 0 0 / 0.12); }
.mcw-note { margin: 8px 0 0; color: var(--ink2); font-size: 12px; }
.mcw-th { display: flex; align-items: flex-start; justify-content: space-between; margin: 30px 0 10px; }
.mcw-th .mcw-gh { margin: 0 0 2px; }
.mcw-th p { margin: 0; color: var(--ink2); }
.mcw-obtn { display: inline-flex; align-items: center; gap: 6px; height: 32px; border: 1px solid var(--card-edge); border-radius: 8px; background: var(--surface); padding: 0 12px 0 10px; font-weight: 600; white-space: nowrap; }
.mcw-tax { display: flex; align-items: center; gap: 14px; min-height: 52px; border-top: 1px solid var(--hair); padding: 8px 16px; }
.mcw-tax:first-child { border-top: 0; }
.mcw-tn { display: flex; flex: 1; flex-wrap: wrap; align-items: center; gap: 2px 8px; min-width: 0; }
.mcw-tn b { font-size: 14px; font-weight: 600; }
.mcw-tn small { flex-basis: 100%; color: var(--ink2); font-size: 12px; }
.mcw-tax > b { font-size: 14px; font-weight: 600; }
.mcw-def { display: inline-flex; align-items: center; gap: 4px; border-radius: 4px; background: var(--selected); padding: 1px 6px; color: var(--ink2); font-size: 11px; font-style: normal; font-weight: 600; }
.mcw-def::before { content: ""; width: 4px; height: 4px; border-radius: 50%; background: currentColor; }

/* ---------- the Saved toast (app_toaster.dart: 320 wide, bottom right, 80 above the bottom on desktop) ---------- */
.mcw-toast { position: absolute; z-index: 4; right: 16px; bottom: 80px; display: flex; align-items: center; gap: 8px; width: 320px; max-width: calc(100% - 32px); border: 1px solid var(--hair); border-radius: 8px; background: var(--surface); padding: 8px 8px 8px 12px; color: var(--ink); font-size: 14px; font-weight: 500; box-shadow: 2px 4px 16px rgb(0 0 0 / 0.16), 0 2px 4px rgb(0 0 0 / 0.08); animation: mcw-toast 0.4s ease 0.3s both; }
.mcw-toast span { flex: 1; min-width: 0; padding: 4px 0; }
.mcw-tok { color: var(--green-t); }
.mcw-tx { color: var(--ink3); }
/* Desktop is the app's compact density: 13px message, 6px padding, 2px before the 28px close button. */
.mcw-desk .mcw-toast { gap: 0; padding: 6px 6px 6px 12px; font-size: 13px; }
.mcw-desk .mcw-toast span { margin-left: 8px; }
.mcw-desk .mcw-tx { box-sizing: content-box; margin-left: 2px; padding: 6px; }

/* ---------- the currency picker dialog (workspace_currency_picker.dart) ---------- */
.mcw-barrier { position: absolute; z-index: 5; inset: 0; display: grid; place-items: center; background: rgb(0 0 0 / 0.34); padding: 24px; }
.mcw-dlg { display: flex; flex-direction: column; width: min(540px, 100%); height: 100%; max-height: 650px; overflow: hidden; border: 1px solid var(--hair); border-radius: 18px; background: var(--surface); box-shadow: 0 10px 28px rgb(0 0 0 / 0.13); }
.mcw-dlg.is-in, .mcp-barrier { animation: mcw-in 0.32s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
.mcw-dh { display: flex; flex: none; align-items: center; gap: 12px; padding: 18px 16px 12px 18px; }
.mcw-dh b { flex: 1; font-size: 16px; font-weight: 700; letter-spacing: -0.2px; }
.mcw-fi { display: grid; flex: none; place-items: center; width: 40px; height: 40px; border-radius: 10px; background: rgb(from var(--blue) r g b / 0.12); color: var(--blue); }
.mcw-fi .ic { width: 20px; height: 20px; }
.mcw-dx { color: var(--ink2); }
.mcw-field { display: flex; flex: none; align-items: center; gap: 10px; height: 44px; margin: 4px 16px 12px; border: 1px solid var(--card-edge); border-radius: 8px; padding: 0 12px; color: var(--ink2); font-size: 14px; }
.mcw-field.typed { border-color: var(--ink); box-shadow: 0 0 0 1px var(--ink); }
.mcw-field .ic { width: 19px; height: 19px; }
.mcw-ph { flex: 1; color: var(--ink3); }
.mcw-q { flex: 1; color: var(--ink); }
.mcw-caret { display: inline-block; width: 1.5px; height: 1.1em; margin: 0 1px; background: var(--ink); vertical-align: text-bottom; animation: mcw-blink 1s steps(1) infinite; }
.mcw-qx { color: var(--ink2); }
.mcw-list { flex: 1; min-height: 0; overflow: hidden; border-top: 1px solid var(--hair); padding: 6px 8px 10px; }
.mcw-cr { display: flex; align-items: center; gap: 10px; margin-bottom: 2px; border-radius: 10px; padding: 9px 10px; }
.mcw-cr.sel { background: var(--tint); }
.mcw-cr.hover { background: var(--selected); }
.mcw-flag { flex: none; width: 30px; color: var(--ink); font-size: 19px; text-align: center; }
.mcw-cn { display: grid; flex: 1; gap: 1px; min-width: 0; }
.mcw-cn b { font-size: 14px; font-weight: 700; letter-spacing: 0.2px; }
.mcw-cn small { color: var(--ink2); font-size: 12px; }
.mcw-sym { flex: none; color: var(--ink2); font-size: 14px; font-weight: 600; }
.mcw-ck { color: var(--primary); }
.mcw--search .mcw-cr { animation: mcw-rise 0.3s ease both; }
.mcw--search .mcw-cr:nth-child(2) { animation-delay: 0.04s; }
.mcw--search .mcw-cr:nth-child(3) { animation-delay: 0.08s; }
.mcw--search .mcw-cr:nth-child(4) { animation-delay: 0.12s; }

@keyframes mcw-in { from { opacity: 0; transform: translateY(10px) scale(0.98); } }
@keyframes mcw-rise { from { opacity: 0; transform: translateY(4px); } }
@keyframes mcw-toast { from { opacity: 0; transform: translateY(8px); } }
@keyframes mcw-toastp { from { opacity: 0; transform: translateY(-8px); } }
@keyframes mcw-wash { from { background: var(--green-f); } to { background: var(--surface); } }
@keyframes mcw-washc { from { background: var(--green-f); } to { background: var(--card); } }
@keyframes mcw-blink { 50% { opacity: 0; } }

/* Tablet: the rail narrows as in the app's tablet render; below 760px it is left out so Regional keeps its width. */
@container mcw (max-width: 1080px) {
  .mcw-desk { grid-template-columns: 220px minmax(0, 1fr); height: 560px; }
  .mcw-page { padding: 18px 28px 0; }
}
@container mcw (max-width: 760px) {
  .mcw-desk { grid-template-columns: minmax(0, 1fr); }
  .mcw-rail { display: none; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.mcw-phone { display: none; }
@container mcw (max-width: 600px) {
  .mcw-desk { display: none; }
  .mcw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: block;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--canvas);
    font-size: calc(var(--t) * 13);
  }
  .mcw-phone .ic { width: calc(var(--t) * 15); height: calc(var(--t) * 15); }
  .mcp-top { position: relative; z-index: 2; display: grid; grid-template-columns: calc(var(--u) * 48) 1fr calc(var(--u) * 48); align-items: center; height: calc(var(--u) * 56); background: var(--rail); padding: 0 calc(var(--u) * 8); }
  .mcp-top .ic { justify-self: center; }
  .mcp-top b { font-size: calc(var(--t) * 15); font-weight: 600; text-align: center; }
  .mcp-body { padding: calc(var(--u) * 16) calc(var(--u) * 16) 0; }
  .mcp-gl { margin: 0 calc(var(--u) * 16) calc(var(--u) * 8); color: var(--ink3); font-size: calc(var(--t) * 10); font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
  .mcp-card { overflow: hidden; border: 1px solid var(--card-edge); border-radius: 10px; background: var(--card); }
  .mcp-row { display: flex; align-items: center; gap: calc(var(--u) * 8); border-top: 1px solid var(--hair); padding: calc(var(--u) * 8) calc(var(--u) * 14); }
  .mcp-row:first-child { border-top: 0; }
  .mcp-row.is-new { animation: mcw-washc 2.4s ease-out both; }
  .mcp-row > span { display: grid; flex: 1; min-width: 0; gap: calc(var(--u) * 1); }
  .mcp-row small { color: var(--ink3); font-size: calc(var(--t) * 10.5); }
  .mcp-row b { overflow: hidden; font-size: calc(var(--t) * 13); font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
  .mcp-row > .ic { color: var(--quiet); }
  .mcp-row.seg { display: grid; gap: calc(var(--u) * 8); }
  .mcp-row.seg .mcw-seg { display: grid; grid-template-columns: 1fr 1fr; padding: calc(var(--u) * 3); font-size: calc(var(--t) * 10.5); }
  .mcp-row.seg .mcw-seg span { padding: calc(var(--u) * 5) 0; text-align: center; }
  .mcp-note { margin: calc(var(--u) * 8) calc(var(--u) * 16) calc(var(--u) * 22); color: var(--ink2); font-size: calc(var(--t) * 10.5); }
  .mcp-tax { display: flex; align-items: center; gap: calc(var(--u) * 12); min-height: calc(var(--u) * 52); border-bottom: 1px solid var(--hair); padding: calc(var(--u) * 8) calc(var(--u) * 14); }
  .mcw-phone .mcw-tn b, .mcp-tax > b { font-size: calc(var(--t) * 12); }
  .mcw-phone .mcw-tn small { font-size: calc(var(--t) * 10); }
  .mcw-phone .mcw-def { font-size: calc(var(--t) * 9.5); }
  .mcp-add { display: flex; align-items: center; gap: calc(var(--u) * 16); height: calc(var(--u) * 48); padding: 0 calc(var(--u) * 16); font-size: calc(var(--t) * 12); font-weight: 600; }
  .mcw-phone .mcw-toast { top: calc(var(--u) * 12); right: calc(var(--u) * 12); bottom: auto; left: calc(var(--u) * 12); width: auto; max-width: none; gap: calc(var(--u) * 8); padding: calc(var(--u) * 8) calc(var(--u) * 8) calc(var(--u) * 8) calc(var(--u) * 12); font-size: calc(var(--t) * 12); animation-name: mcw-toastp; }

  /* the bottom sheet over the keyboard (the search field has focus) */
  .mcp-barrier { position: absolute; z-index: 5; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; background: rgb(0 0 0 / 0.34); }
  .mcp-sheet { display: flex; flex-direction: column; height: calc(var(--u) * 352); margin: 0 0 calc(var(--u) * 8); overflow: hidden; border: 1px solid var(--hair); border-radius: calc(var(--u) * 26); background: var(--surface); box-shadow: 0 10px 28px rgb(0 0 0 / 0.13); }
  .mcp-handle { flex: none; width: calc(var(--u) * 38); height: calc(var(--u) * 4); margin: calc(var(--u) * 8) auto calc(var(--u) * 2); border-radius: 2px; background: rgb(from var(--ink) r g b / 0.18); }
  .mcw-phone .mcw-dh { gap: calc(var(--u) * 12); padding: calc(var(--u) * 5) calc(var(--u) * 14) calc(var(--u) * 10) calc(var(--u) * 18); }
  .mcw-phone .mcw-dh b { font-size: calc(var(--t) * 14); }
  .mcw-phone .mcw-fi { width: calc(var(--u) * 40); height: calc(var(--u) * 40); }
  .mcw-phone .mcw-fi .ic { width: calc(var(--u) * 20); height: calc(var(--u) * 20); }
  .mcw-phone .mcw-field { gap: calc(var(--u) * 10); height: calc(var(--u) * 44); margin: calc(var(--u) * 2) calc(var(--u) * 16) calc(var(--u) * 10); padding: 0 calc(var(--u) * 12); font-size: calc(var(--t) * 12); }
  .mcw-phone .mcw-field .ic { width: calc(var(--u) * 19); height: calc(var(--u) * 19); }
  .mcw-phone .mcw-ph .mcw-caret { margin: 0 calc(var(--u) * 2) 0 0; }
  .mcw-phone .mcw-list { padding: calc(var(--u) * 6) calc(var(--u) * 8) 0; }
  .mcw-phone .mcw-cr { gap: calc(var(--u) * 10); margin-bottom: calc(var(--u) * 2); padding: calc(var(--u) * 8) calc(var(--u) * 10); }
  .mcw-phone .mcw-flag { width: calc(var(--u) * 30); font-size: calc(var(--u) * 19); }
  .mcw-phone .mcw-cn b, .mcw-phone .mcw-sym { font-size: calc(var(--t) * 12); }
  .mcw-phone .mcw-cn small { font-size: calc(var(--t) * 10); }
  .mcp-kb { flex: none; display: grid; gap: calc(var(--u) * 11); height: calc(var(--u) * 216); background: var(--kb); padding: calc(var(--u) * 8) calc(var(--u) * 3) 0; }
  .mcp-kr { display: flex; justify-content: center; gap: calc(var(--u) * 6); }
  .mcp-kr:nth-child(2) { padding: 0 calc(var(--u) * 18); }
  .mcp-k { display: grid; flex: 1; place-items: center; max-width: calc(var(--u) * 34); height: calc(var(--u) * 42); border-radius: calc(var(--u) * 5); background: var(--key); color: var(--ink); font-family: ui-sans-serif, system-ui, sans-serif; font-size: calc(var(--u) * 20); box-shadow: 0 1px 0 rgb(0 0 0 / 0.3); }
  .mcp-k.fn { max-width: calc(var(--u) * 42); background: var(--key-fn); font-size: calc(var(--u) * 16); }
  .mcp-kr:nth-child(3) .mcp-k.fn:first-child { margin-right: calc(var(--u) * 8); }
  .mcp-kr:nth-child(3) .mcp-k.fn:last-child { margin-left: calc(var(--u) * 8); }
  .mcp-k.w2 { max-width: calc(var(--u) * 88); font-size: calc(var(--u) * 15); }
  .mcp-k.space { max-width: none; font-size: calc(var(--u) * 15); }
  .mcp-k.go { background: #0a84ff; color: #fff; }
}
@media (prefers-reduced-motion: reduce) { .mcw * { animation: none !important; transition: none !important; } }
</style>
